/**
 * CheckoutModal — modal para iniciar suscripción a un tier.
 *
 * Pasos:
 *   1. Coach elige modo: Recurrente automático (default) o Pago manual mensual
 *   2. Click "Continuar" → POST /subscriptions/checkout → recibe init_point
 *   3. Redirect a MP (window.location = init_point)
 *
 * Después MP procesa y redirige a /payments/return con status.
 */
import { useState } from "react";
import { Icon } from "../lib/Icon";
import { useToast } from "../lib/toast";
import { createCheckout, type SubscriptionTier, type SubscriptionMode } from "../api/subscriptions";
import { validateCoupon, type CouponValidation } from "../api/coupons";

interface CheckoutModalProps {
  tier: SubscriptionTier;
  tierLabel: string;
  amountCop: number;
  onClose: () => void;
}

export function CheckoutModal({ tier, tierLabel, amountCop, onClose }: CheckoutModalProps) {
  const toast = useToast();
  const [mode, setMode] = useState<SubscriptionMode>("auto");
  const [submitting, setSubmitting] = useState(false);

  // Cupón de descuento
  const [coupon, setCoupon] = useState("");
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [couponResult, setCouponResult] = useState<CouponValidation | null>(null);
  const discountApplied =
    couponResult?.valid && couponResult.kind === "discount" && couponResult.final_price_cop != null;
  const finalAmount = discountApplied ? couponResult!.final_price_cop! : amountCop;

  const handleApplyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    setCheckingCoupon(true);
    try {
      const res = await validateCoupon(code, tier);
      setCouponResult(res);
      if (res.valid && res.kind === "free_months") {
        // Un cupón de meses gratis no se canjea acá (es para la cuenta del coach,
        // no para reducir un pago). Avisamos para que lo use en Configuración.
        toast("Ese es un cupón de meses gratis — canjealo en Configuración › Mi suscripción.");
      }
    } catch (e) {
      setCouponResult({ valid: false, message: e instanceof Error ? e.message : "Cupón inválido" });
    } finally {
      setCheckingCoupon(false);
    }
  };

  const handleStart = async () => {
    setSubmitting(true);
    try {
      const resp = await createCheckout(tier, mode, discountApplied ? coupon.trim() : null);
      // PRODUCCIÓN: usamos siempre `init_point` (URL real de MP).
      // `sandbox_init_point` solo aplica si las credenciales del backend son TEST-*.
      // Como nuestro backend en prod tiene APP_USR-*, init_point va a mercadopago.com.co.
      const url = resp.init_point || resp.sandbox_init_point;
      if (!url) {
        toast("MercadoPago no devolvió URL de pago");
        setSubmitting(false);
        return;
      }
      // Redirigir al coach a MP
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast("No pude iniciar el pago: " + msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div className="al-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 20 }}>
              Suscribirse a {tierLabel}
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {discountApplied ? (
                <>
                  <span style={{ textDecoration: "line-through", opacity: 0.6 }}>
                    ${amountCop.toLocaleString("es-CO")}
                  </span>{" "}
                  <b style={{ color: "var(--primary)" }}>
                    ${finalAmount.toLocaleString("es-CO")} COP / mes
                  </b>
                </>
              ) : (
                <>${amountCop.toLocaleString("es-CO")} COP / mes</>
              )}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} disabled={submitting}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="tiny muted" style={{ lineHeight: 1.5 }}>
            Pagás con tarjeta, PSE o efectivo a través de MercadoPago. Tu información de pago
            no pasa por AetherLite.
          </div>

          <div>
            <div className="al-pb-side-eyebrow" style={{ marginBottom: 8 }}>
              ¿TENÉS UN CUPÓN?
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="al-input"
                value={coupon}
                onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponResult(null); }}
                placeholder="Código de descuento"
                style={{ flex: 1, textTransform: "uppercase" }}
                onKeyDown={(e) => { if (e.key === "Enter") handleApplyCoupon(); }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleApplyCoupon}
                disabled={checkingCoupon || !coupon.trim()}
              >
                {checkingCoupon ? "…" : "Aplicar"}
              </button>
            </div>
            {couponResult && (
              <div
                className="tiny"
                style={{ marginTop: 6, color: couponResult.valid && discountApplied ? "var(--primary)" : "var(--danger, #dc2626)" }}
              >
                {couponResult.valid && discountApplied
                  ? `✓ Descuento aplicado — ahora $${finalAmount.toLocaleString("es-CO")} COP/mes`
                  : couponResult.message}
              </div>
            )}
          </div>

          <div>
            <div className="al-pb-side-eyebrow" style={{ marginBottom: 8 }}>
              MODO DE COBRO
            </div>
            <CheckoutModeOption
              value="auto"
              current={mode}
              onSelect={() => setMode("auto")}
              title="Recurrente automático"
              desc="MercadoPago cobra automáticamente cada mes. Cancelás cuando quieras desde Configuración."
              recommended
            />
            <CheckoutModeOption
              value="manual"
              current={mode}
              onSelect={() => setMode("manual")}
              title="Pago manual mensual"
              desc="Pagás un mes ahora. Te avisamos por email cuando se acerque el vencimiento para que renueves vos."
            />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={submitting} style={{ flex: 1 }}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleStart}
              disabled={submitting}
              style={{ flex: 1 }}
            >
              {submitting ? "Redirigiendo…" : "Continuar a MercadoPago"}
            </button>
          </div>

          <div className="tiny muted" style={{ textAlign: "center", lineHeight: 1.4 }}>
            Al continuar aceptás los <a href="#/terms">Términos</a> y la{" "}
            <a href="#/privacy">Política de Privacidad</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

interface CheckoutModeOptionProps {
  value: SubscriptionMode;
  current: SubscriptionMode;
  onSelect: () => void;
  title: string;
  desc: string;
  recommended?: boolean;
}

function CheckoutModeOption({ value, current, onSelect, title, desc, recommended }: CheckoutModeOptionProps) {
  const isActive = current === value;
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        marginBottom: 8,
        border: `1.5px solid ${isActive ? "var(--primary)" : "var(--border)"}`,
        borderRadius: 10,
        background: isActive ? "rgba(124, 58, 237, 0.06)" : "var(--bg)",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: isActive ? "var(--primary)" : "var(--fg)" }}>
          {title}
        </span>
        {recommended && (
          <span style={{
            background: "var(--primary)",
            color: "var(--primary-fg, #fff)",
            fontSize: 9,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            letterSpacing: "0.06em",
          }}>
            RECOMENDADO
          </span>
        )}
      </div>
      <div className="tiny muted" style={{ lineHeight: 1.4 }}>
        {desc}
      </div>
    </button>
  );
}
